import React, { useCallback, useEffect, useState } from 'react';
import {
  RecordContextProvider,
  useRecordContext,
  useStore,
} from 'react-admin';
import {
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import { Cancel } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { MemoryRouter, useLocation } from 'react-router-dom';

import useDialogStatus from '../../hooks/useDialogStatus';
import useOverriddenExpectedBehaviours from '../../hooks/useOverriddenExpectedBehaviours';
import { TransactionTypeConfiguration } from './AddTransactionTypeDialogButton';
import TransactionTypeConfigurationField from '../../customFields/TransactionTypeConfigurationField';
import DatapointStatusField from './DatapointStatusField';
import { EntityTypes } from '../../constants/entityTypes';
import ConflictResolution from '../conflict/ConflictResolution';
import conflictFields from '../../constants/conflictFields';
import { TransactionMonitoringThresholds } from '../../utilities/schemas/datapoints/transactionMonitoringThresholds';

const OverrideExpectedBehaviourButton = ({
  typeConfiguration,
  getOverride,
  setOverride,
}: {
  typeConfiguration: TransactionTypeConfiguration | undefined;
  getOverride: (
    typeConfigurationKey: string | undefined,
  ) => TransactionMonitoringThresholds | undefined;
  setOverride: (
    typeConfigurationKey: string | undefined,
    value: TransactionMonitoringThresholds | undefined,
  ) => void;
}) => {
  const record = useRecordContext();

  const typeConfigurationKey = typeConfiguration
    ? JSON.stringify(typeConfiguration) : undefined;

  const currentOverridenValue = getOverride(typeConfigurationKey);
  const isCurrentlyCompared = isEqual(currentOverridenValue, record?.value);

  const startComparing = useCallback(() => {
    if (!record?.value) return;

    setOverride(typeConfigurationKey, record.value);
  }, [record?.value, setOverride, typeConfigurationKey]);

  const stopComparing = useCallback(() => {
    setOverride(typeConfigurationKey, undefined);
  }, [setOverride, typeConfigurationKey]);

  return (
    <Button
      variant={isCurrentlyCompared ? 'contained' : 'outlined'}
      onClick={isCurrentlyCompared ? stopComparing : startComparing}
      size="medium"
    >
      Compare in table
    </Button>
  );
};

const ExpectedBehaviourFilter = ({
  entityId,
  entityType,
}: {
  entityId: string;
  entityType: EntityTypes;
}) => {
  const { open, openDialog, closeDialog } = useDialogStatus();
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = useCallback((_event: React.SyntheticEvent, index: number) => {
    setTabIndex(index);
  }, []);

  const [
    typeConfigurations,
  ] = useStore<(TransactionTypeConfiguration | undefined)[]>('transactionTable', [undefined]);

  const {
    getOverride,
    setOverride,
    resetOverrides,
  } = useOverriddenExpectedBehaviours(entityId);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      resetOverrides();
    };
  }, [resetOverrides]);

  const currentTypeConfiguration = typeConfigurations[tabIndex];

  return (
    <>
      <Button onClick={openDialog} startIcon={<FilterListIcon />}>
        Filter expected behavior
      </Button>
      <Dialog open={open} onClose={closeDialog} maxWidth="xl" fullWidth>
        <DialogTitle color="primary">
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography>Filter expected behavior</Typography>
            <IconButton onClick={closeDialog}>
              <Cancel />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {typeConfigurations.map((typeConfiguration) => (
              <Tab
                key={JSON.stringify(typeConfiguration) ?? 'undefined'}
                label={(
                  <RecordContextProvider value={{ typeConfiguration }}>
                    <Box display="flex" gap={2} alignItems="center">
                      <TransactionTypeConfigurationField source="typeConfiguration" />
                      <DatapointStatusField entityId={entityId} entityType={entityType} />
                    </Box>
                  </RecordContextProvider>
                )}
              />
            ))}
          </Tabs>
          <Divider />
          <Box paddingTop={8}>
            <MemoryRouter
              initialEntries={[
                `${location.pathname}?name=${conflictFields.TRANSACTION_MONITORING_THRESHOLD}${currentTypeConfiguration ? `&identificationInfo=${JSON.stringify(currentTypeConfiguration)}` : ''}`,
              ]}
            >
              <ConflictResolution
                entityId={entityId}
                entityType={entityType}
                hideBackButton
                customValueActions={(
                  <OverrideExpectedBehaviourButton
                    getOverride={getOverride}
                    setOverride={setOverride}
                    typeConfiguration={currentTypeConfiguration}
                  />
                )}
              />
            </MemoryRouter>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExpectedBehaviourFilter;
